<script setup>
/**
 * Aceleres: card de "respondendo a", no formato do WhatsApp.
 *
 * Antes era uma faixa cinza com "Respondendo a: Foto" — sem autor, sem cor e
 * sem distinguir os tipos de mídia. Agora traz a barra colorida do autor, o
 * nome dele e, embaixo, o conteúdo citado: figurinha vira miniatura pequena;
 * o resto (foto, vídeo, GIF, áudio, documento, contato, localização, link)
 * vira ícone + rótulo, e texto continua texto.
 *
 * Quem responde "o que mostrar" é o helper quotedMessagePreview — o mesmo que
 * a citação dentro do balão usa, para as duas não divergirem.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { describeQuotedMessage } from 'dashboard/helper/quotedMessagePreview';
import LottieStickerCanvas from 'dashboard/components-next/message/bubbles/LottieStickerCanvas.vue';
import QuotedMediaIcon from 'dashboard/components-next/message/QuotedMediaIcon.vue';
import { stripGroupSenderPrefix } from 'dashboard/components-next/message/groupParticipant.js';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['dismiss']);

const { t } = useI18n();
const { getPlainText } = useMessageFormatter();

const quoted = computed(() => describeQuotedMessage(props.message, t));

const quotedText = computed(() => {
  const content = stripGroupSenderPrefix(
    props.message?.content,
    props.message?.content_attributes || {}
  );
  return getPlainText(content || '');
});

// Figurinha não tem legenda; nas demais, o texto da mensagem tem prioridade
// sobre o rótulo do anexo (uma foto com legenda mostra a legenda).
const label = computed(() => {
  if (quoted.value.isSticker) return quoted.value.label;
  if (quotedText.value) return quotedText.value;
  return quoted.value.label || t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// Aceleres 2026-08-17: o icone da midia deixou de sumir quando ha legenda.
// A regra antiga (`quotedText ? '' : kind`) escondia justamente o caso mais
// comum — foto/video/GIF COM legenda —, e o card ficava so com o texto, sem
// dizer a que tipo de midia se estava a responder.
const iconKind = computed(() => quoted.value.kind);

// Aceleres 2026-08-17: com legenda o WhatsApp empilha — o icone numa linha e a
// legenda na seguinte; sem legenda ficam lado a lado, com o icone antes do
// rotulo ("Foto", "Video"). A figurinha nao entra aqui: ela tem miniatura
// propria e nunca tem legenda.
const hasMediaCaption = computed(
  () => Boolean(quoted.value.kind) && Boolean(quotedText.value)
);
</script>

<template>
  <div
    class="acl-reply-to-card relative flex items-stretch overflow-hidden rounded-lg bg-n-slate-3"
  >
    <span
      class="w-1 flex-shrink-0"
      :style="{ backgroundColor: quoted.accentColor }"
      aria-hidden="true"
    />
    <div
      class="flex min-w-0 flex-1 flex-col justify-center gap-1 py-2 pl-3 pr-12"
    >
      <!-- Aceleres 2026-08-17: a linha do AUTOR saiu do cartao de resposta a
           pedido — nem nome de contato, nem "Voce"/"Tu". Quem esta a
           responder acabou de escolher a mensagem e ja sabe de quem e; o que
           precisa de ver aqui e o CONTEUDO citado.
           A cor do autor (`quoted.accentColor`) continua a ser usada: e ela
           que pinta a barra vertical a esquerda, e e por ai que a autoria
           continua a ler-se sem ocupar uma linha. -->
      <div
        class="flex min-w-0 text-[13px] leading-tight text-n-slate-11"
        :class="hasMediaCaption ? 'flex-col gap-0.5' : 'items-center gap-1.5'"
      >
        <LottieStickerCanvas
          v-if="quoted.stickerLottieUrl"
          :key="quoted.stickerLottieUrl"
          :url="quoted.stickerLottieUrl"
          class="size-10 flex-shrink-0"
        />
        <img
          v-else-if="quoted.stickerUrl"
          :src="quoted.stickerUrl"
          :alt="quoted.label"
          class="size-10 flex-shrink-0 object-contain"
        />
        <template v-else>
          <QuotedMediaIcon
            v-if="iconKind"
            :kind="iconKind"
            :icon="quoted.icon"
          />
          <span class="truncate">{{ label }}</span>
        </template>
      </div>
    </div>
    <!-- Aceleres 2026-08-17: o X fica CENTRADO na altura do cartao.
         Estava ancorado no topo porque havia uma linha de autor acima do
         texto e ele alinhava com ela. Tirada essa linha, o cartao passou a ter
         uma linha so e o topo deixou de ser referencia: o X ficava colado a
         borda de cima e torto em relacao ao texto.
         O botao tambem encolheu de 44px para 36px — o cartao de uma linha tem
         cerca de 34px de altura e o cartao TEM `overflow-hidden`, entao um
         alvo maior do que ele ficava cortado em cima e em baixo. O X em si
         continua grande (28px). `right-3` da a folga lateral que faltava. -->
    <button
      v-tooltip="$t('CONVERSATION.REPLYBOX.DISMISS_REPLY')"
      type="button"
      class="absolute right-3 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-slate-4"
      :aria-label="$t('CONVERSATION.REPLYBOX.DISMISS_REPLY')"
      @click.stop="emit('dismiss')"
    >
      <!-- O `size` do <span> e o traco visivel; o do <button> e a area de
           clique e o circulo do hover. Mantem-se a folga entre os dois: o
           icone nunca pode chegar ao tamanho do alvo, senao vaza do circulo. -->
      <span class="i-lucide-x size-7" aria-hidden="true" />
    </button>
  </div>
</template>
